import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQuery } from 'src/shared/lib/useQuery';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import { useOrg } from 'src/shared/contexts/OrgContext';

const BASE_URL = '/api/v2/account/invites';

export type invitesResponseType = {
  id: string;
  organisation: { id: string; name?: string };
  sender: { email?: string };
};

export function usePendingInvites() {
  const { orgFetch, currentOrg } = useOrg();

  return useQuery({
    queryKey: ['pending-invites'],
    queryFn: getData,
    enabled: !!currentOrg.id,
    staleTime: 1,
  });

  async function getData() {
    const res = await orgFetch<invitesResponseType[]>(BASE_URL);
    const data = await res.json();
    if (!res.ok) {
      throw new Error('Failed to fetch pending invites: ');
    }

    return data;
  }
}

export function useMutateInvites() {
  const { apiFetch } = useAuth();
  const queryClient = useQueryClient();

  const acceptInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await apiFetch(`${BASE_URL}/${inviteId}/accept`, {
        method: 'POST',
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(JSON.stringify(data));
      }

      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-invites'] });
      queryClient.invalidateQueries({ queryKey: ['orgs'] });
    },
    onError: (error) => {
      console.error('Error accepting invite:', error);
    },
  });

  const denyInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await apiFetch(`${BASE_URL}/${inviteId}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(JSON.stringify(data));
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-invites'] });
    },
    onError: (error) => {
      console.error('Error denying invite:', error);
    },
  });

  return { acceptInvite, denyInvite };
}
